import { ClickEvent, Component, Container, DataDisplayEvent, DataHeader, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownSelectionEvent, LookupModelSelectionEvent, SelectionType, StepEvent, TableContentsChangedEvent, TableRowCreationEvent, TableRowMode, TableSelectionEvent } from "@mcleod/components";
import { ArrayUtil, ModelRow, StringUtil, UrlUtil } from "@mcleod/core";
import { ResponsibilityUtil, UsersResponsibilitySlideoutMF } from "@mcleod/general";
import { AutogenLayoutCallinScriptStepper } from "./autogen/AutogenLayoutCallinScriptStepper";

export enum WorkflowStep { TEMPLATE = 0, CALLIN = 1, REVIEW = 2 }

const fieldMap = new Map();
fieldMap.set("orders", ["blnum", "bol_received", "bol_recv_date"]);
fieldMap.set("movement", ["carrier_contact", "carrier_email", "carrier_phone", "carrier_tractor", "carrier_trailer", "override_driver_nm", "override_drv_cell"]);
fieldMap.set("stop", ["cases", "hub", "pallets_dropped", "pallets_picked_up", "volume", "weight"]);


export class CallinScriptStepper extends AutogenLayoutCallinScriptStepper {
    private _isFromSelection: boolean = false;

    continueButtonOnClick(event: ClickEvent) {
        this.stepperCallinScript.selectedIndex = this.stepperCallinScript.selectedIndex + 1;
    }

    override onLoad() {
        this.sourceCallinScriptTemplate.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => {
            this.sourceCallinScriptAfterGetDataboundValues(row, dataSource);
        }
        this.sourceCallinScriptTemplate.addAfterModeChangeListener(async () => {
            if (this.sourceCallinScriptTemplate.mode == DataSourceMode.ADD) {
                this.buttonCallinsUser.visible = false;
            }
            if (this.sourceCallinScriptTemplate.mode == DataSourceMode.UPDATE) {
                const isCallinScriptEnabled = await ResponsibilityUtil.isFilterEnabled("callin_script_agency_filter");
                this.buttonCallinsUser.visible = isCallinScriptEnabled;
            }
        });
    }

    async stepperCallinScriptBeforeStepListener(event: StepEvent) {
        if (event.oldIndex === WorkflowStep.TEMPLATE) { // if the user is on the first step going to the second
            if ((!this.step1.validateSimple())) {
                event.preventDefault();
            }
            else {
                this.panelDetails.displayData(this.sourceCallinScriptTemplate.activeRow, null, 0);
            }
        }
        else if (event.oldIndex === WorkflowStep.CALLIN && event.newIndex === WorkflowStep.REVIEW) { // if the user is on the second step and is moving to the third step
            this.buttonContinue.visible = false;
            this.layoutCallinScript.clearScripts();
            const sourceCallin = this.layoutCallinScript.sourceCallin;
            sourceCallin.createBlankRow().then(row => {
                row.setValues(this.sourceCallinScriptTemplate.activeRow.data);
                sourceCallin.setRowsAndMode(DataSourceMode.ADD, [row]);
                const details = [];
                this.sourceCallinScriptTemplateDetail.data.forEach((row) => {
                    details.push(row.data);
                });
                sourceCallin.activeRow.set("callin_script_details", details);
                this.layoutCallinScript.buildScript(null, true);
            });
        }
        else if (event.oldIndex > event.newIndex) { // if the user going from the third step back to the second/first step
            this.buttonContinue.visible = true;
        }
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.showDelete = true;
    }

    textboxUserEntersBeforeLookupModelSearch(event) {
        event.filter.table_name = "callin"
    }

    async buttonAddPromptOnClick(event: ClickEvent) {
        if (this.panelPromptDetails.validateSimple()) {
            this.tableAddedPrompts.addRow(this.sourceCallinScriptTemplateDetailEdit.activeRow, { mode: TableRowMode.ADD }, { display: true, addToData: true });
            this.sourceCallinScriptTemplateDetailEdit.setRowsAndMode(DataSourceMode.ADD, [await this.sourceCallinScriptTemplateDetailEdit.createBlankRow()]);
        }
    }

    tableAddedPromptsOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.getTableRow();
        tableRow.height = 100;
        tableRow.draggable = false;
        if (tableRow.data._appending) {
            tableRow.mode = TableRowMode.NONE;
        }
    }

    tableAddedPromptsOnSelect(event: TableSelectionEvent) {
        if (this.tableAddedPrompts.selectedRow != null) {
            const modelRow = this.tableAddedPrompts.selectedRow.data.createBasicCopy();
            this.buttonDelete.enabled = true;
            this._isFromSelection = true;
            this.sourceCallinScriptTemplateDetailEdit.setRowsAndMode(DataSourceMode.UPDATE, [modelRow]);
            this._isFromSelection = false;
        }
        else {
            this.sourceCallinScriptTemplateDetailEdit.mode = DataSourceMode.ADD;
        }
    }

    sourceCallinScriptTemplateDetailEditAfterModeChange(event: DataSourceModeChangeEvent) {
        const addMode = event.newMode === DataSourceMode.ADD;
        this.panelUpdate.visible = !addMode;
        this.panelAdd.visible = addMode;
        this.buttonDelete.enabled = !addMode;
    }

    sourceCallinScriptTemplateBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() !== DataSourceAction.SEARCH) {
            let hasRequiredPrompts = false;
            this.sourceCallinScriptTemplateDetail.data.forEach((row, index) => {
                if (row.get("callin_script_template_id") == null) {
                    row.set("callin_script_template_id", this.mainDataSource.activeRow.get("id"));
                }
                row.set("sequence", index)
                if (row.get("is_required") == "Y") {
                    hasRequiredPrompts = true;
                }
            });
            this.mainDataSource.activeRow.set("is_required", hasRequiredPrompts ? "Y" : "N");
        }
    }

    sourceCallinScriptTemplateDetailAfterExecution(event: DataSourceExecutionEvent) {
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        if (urlParams?.detailIdToOpen) {
            this.stepperCallinScript.selectedIndex = WorkflowStep.CALLIN;
            this.tableAddedPrompts.rows.forEach((row) => {
                if (row.data.get("id") == urlParams.detailIdToOpen) {
                    this.tableAddedPrompts.selectRow(row, SelectionType.SINGLE, null);
                }
            });
        }
    }

    async sourceCallinScriptTemplateAfterModeChange(event: DataSourceModeChangeEvent) {
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        if (event.newMode == DataSourceMode.ADD) {
            this.dataheaderCallinScriptStepper.title = "Add Callin Script Template";
            if (urlParams?.idToCopy) {
                await this.sourceCallinScriptTemplateDetailEdit.search({ callin_script_template_id: urlParams.idToCopy });
                const rowsToCopy = [];
                this.sourceCallinScriptTemplateDetailEdit.data.forEach((row) => {
                    const copy = row.createBasicCopy();
                    copy.set("callin_script_template_id", null);
                    copy.set("id", null);
                    copy._appending = true;
                    rowsToCopy.push(copy);
                });
                this.sourceCallinScriptTemplateDetail.setRowsAndMode(DataSourceMode.ADD, rowsToCopy);
                this.clearEdits();
            }
        }
        else if (event.newMode == DataSourceMode.UPDATE) {
            this.dataheaderCallinScriptStepper.title = "Edit Callin Script Template";
            if (urlParams?.detailIdToOpen) {
                this.stepperCallinScript.selectedIndex = WorkflowStep.CALLIN;
            }
            else {
                this.sourceCallinScriptTemplateDetailEdit.mode = DataSourceMode.ADD;
            }

        }

    }

    textboxDetailTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.decisionTreeInputChange();
    }

    textboxDetailTypeOnDataDisplay(event: DataDisplayEvent) {
        this.decisionTreeInputChange()
    }

    buttonSaveEditsOnClick(event: ClickEvent) {
        if (this.panelPromptDetails.validateSimple()) {
            const tableRow = this.tableAddedPrompts.selectedRow;
            const modelRow = tableRow.data;
            modelRow.resetOriginalData();
            if (this.sourceCallinScriptTemplateDetailEdit.activeRow.get("save_data_field") == null &&
                this.sourceCallinScriptTemplateDetailEdit.activeRow.getLookupModelData("save_data_field") != null) {
                const lmDataRow = this.sourceCallinScriptTemplateDetailEdit.activeRow.getFirstLookupModelData("save_data_field");
                this.sourceCallinScriptTemplateDetailEdit.activeRow.set("save_data_field", lmDataRow?.get("value"));
            }
            modelRow.setValues(this.sourceCallinScriptTemplateDetailEdit.activeRow.data)
            if (modelRow.get("is_required") == null) {
                modelRow.set("is_required", "N");
            }
            if (modelRow.get("update_field") == null) {
                modelRow.set("update_field", "N");
            }
            if (this.mainDataSource.mode === DataSourceMode.ADD || this.mainDataSource.mode === DataSourceMode.UPDATE) {
                this.tableAddedPrompts.redisplaySingleRow(tableRow.index, modelRow, false, true);
            }
            this.clearEdits();
        }
    }

    buttonCancelEditOnClick(event: ClickEvent) {
        this.clearEdits();
    }

    clearEdits() {
        if (this.tableAddedPrompts.selectedRow) {
            this.tableAddedPrompts.selectedRow = null;
        }
        this.sourceCallinScriptTemplateDetailEdit.mode = DataSourceMode.ADD;
        this.buttonDelete.enabled = false
    }

    buttonDeleteOnClick(event: ClickEvent) {
        this.tableAddedPrompts.deleteRow(this.tableAddedPrompts.selectedRow.index);
        this.buttonDelete.enabled = false;
        this.clearEdits();
    }

    tableAddedPromptsOnContentsChanged(event: TableContentsChangedEvent) {
        this.buttonDelete.enabled = this.tableAddedPrompts.selectedRow !== null;
    }

    textboxTablNameBeforeLookupModelSearch(event) {
        const tableName = this.sourceCallinScriptTemplateDetailEdit.activeRow.get("table_name");

        if (tableName != null) {
            event.filter.table_name = tableName;
        } else {
            event.preventDefault();
        }
    }

    textboxOrderTypeBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
    }

    textboxTableOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.switchIncludeInputField.checked = false;
        this.switchDisplayStoreValue.checked = false;
        this.decisionTreeInputChange()
    }

    decisionTreeInputChange() {
        this.resetComponentVisibiltyAndValidation();
        if (!this._isFromSelection) {
            this.resetComponentValue();
        }
        const row = this.sourceCallinScriptTemplateDetailEdit.activeRow;
        if (row != null) {
            const detailType = row.get("detail_type");
            const tableName = row.get("table_name");
            const fieldName = row.getFirstLookupModelData("field_name") ?? this.textboxFieldName.getFirstLookupModelData();

            if (this.textboxDetailType.selectedItem && this.sourceCallinScriptTemplateDetailEdit.activeRow) {
                this.callinScriptDecisionTree(detailType, tableName, fieldName, row.getBoolean("include_input_field"));
            }
        }
    }

    resetComponentVisibiltyAndValidation() {
        this.panelPromptDetails.forEveryChildComponent((component) => {
            component.setProps({ visible: true, required: false });
        })
    }

    resetComponentValue() {
        if (this.sourceCallinScriptTemplateDetailEdit.activeRow?.get("table_name") !== "stop") {
            this.clearAndDisableComponents(this.textboxStop);
        }

        this.clearAndDisableComponents(
            this.switchDisplayTotal,
            this.switchInputFieldRequired,
            this.switchUpdateField,
            this.textboxUserEnters
        );
    }

    callinScriptDecisionTree(detailType: string, table: string, field: ModelRow, includeInputField: boolean = false) {
        switch(detailType) {
            case "C": {
                this.textboxTable.required = true;
                this.textboxFieldName.required = true;
                this.decisionBasedOnTable(table, includeInputField);
                this.switchDisplayTotal.visible = (table == "movement" && field?.get("field_name") == "override_pay_amt");
                if (includeInputField) {
                    this.whatUserEntersCheck(table, field);
                }
                else {
                    this.clearAndDisableComponents(this.switchInputFieldRequired, this.textboxUserEnters, this.switchUpdateField);
                }
                break;
            }
            case "P": {
                this.panelPromptDetails.forEveryChildComponent((component) => {
                    if (!(component instanceof Container)
                        && component !== this.textboxDetailType
                        && component !== this.textboxPrompt) {
                        this.clearAndDisableComponents(component);
                    }
                });
                this.textboxPrompt.required = true;
                break;
            }
            case "T":
            case "O": {
                this.clearAndDisableComponents(this.textboxTable, this.textboxFieldName, this.textboxStop);
                if (detailType === "O") {
                    this.clearAndDisableComponents(this.switchDisplayStoreValue);
                }
                if (includeInputField) {
                    this.whatUserEntersCheck(table, field);
                }
                else {
                    this.clearAndDisableComponents(this.switchInputFieldRequired, this.textboxUserEnters, this.switchUpdateField);
                }
                this.clearAndDisableComponents(this.switchDisplayTotal)
                this.decisionBasedOnInclude(includeInputField);
                break;
            }
            default: {
               break;
            }
         }
          if (this.sourceCallinScriptTemplateDetailEdit.mode == DataSourceMode.UPDATE) {
           this.panelSecondPromptDetails.displayData(this.tableAddedPrompts.selectedRow?.data, null, 0);
         }
    }

    decisionBasedOnTable(table: string, includeInputField: boolean) {
        this.decisionBasedOnInclude(includeInputField);
        if ("stop" === table) {
            this.textboxStop.visible = true;
            this.textboxStop.required = true;
        } else {
            this.clearAndDisableComponents(this.textboxStop);
        }
    }

    decisionBasedOnInclude(includeInputField: boolean) {
        this.switchInputFieldRequired.visible = this.textboxUserEnters.visible = includeInputField;
    }

    clearAndDisableComponents(...components: Component[]) {
        components.forEach((comp) => {
            if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
                this.sourceCallinScriptTemplateDetailEdit.activeRow?.set(comp.field, null);
            }
            comp.visible = false;
        });
    }

    whatUserEntersCheck(table: String, field: ModelRow) {
        if (fieldMap.get(table)?.includes(field?.get("field_name"))) {
            this.updateUserEntersTextbox(field?.get("caption"));
        }
        else {
            this.clearAndDisableComponents(this.switchUpdateField);
        }
    }

    updateUserEntersTextbox(caption: string) {
        this.switchUpdateField.visible = true;
        this.switchUpdateField.caption = `Update ${caption} with what the user enters`
    }

    displayCallinsUsersSlideout(event: ClickEvent) {
        const urs = UsersResponsibilitySlideoutMF.builder()
            .layoutName("lme/dispatch/CallinScriptUsers")
            .title(this.getCallinsUsersSlideoutTitle())
            .idFilter(this.getCallinsUsersIdFilter())
            .parentMainDataSource(this.sourceCallinScriptTemplate)
            .parentAgencyLinkDataSource(this.sourceAgencyLink)
            .primaryAgencyField(ResponsibilityUtil.getPrimaryAgencyField())
            .build();
        urs.show();
    }

    private getCallinsUsersIdFilter(): any {
        const callinScript: string = this.sourceCallinScriptTemplate.activeRow?.get("id");
        return callinScript != null ? { id: callinScript } : null;
    }

    private getCallinsUsersSlideoutTitle(): string {
        const id: string = this.sourceCallinScriptTemplate.activeRow?.get("id");
        return `Callins Script Template Users${StringUtil.isEmptyString(id) ? "" : " - " + id}`;
    }

    sourceCallinScriptAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            const primaryAgencyField = ResponsibilityUtil.getPrimaryAgencyField();
            row.set(primaryAgencyField, dataSource.searchRow.get(primaryAgencyField));
            if (ArrayUtil.isEmptyArray(this.sourceAgencyLink.data)) {
                return;
            }
            row.addLinkedModel({ model: this.sourceAgencyLink.url, rows: [this.sourceAgencyLink.data[0]] });
        }
    }

}
